import {
  BookedCustomItem,
  DataRequiredForReception,
  Discount,
  DiscountAppliesTo,
  DiscountCodeOnJob,
  ManualServiceActionInReception,
  Reception,
  Route,
  ServiceBikeType,
  ServiceFee,
  StandardFeeType,
  populateRoute,
} from "@repo/common"
import { queryOptions } from "@tanstack/react-query"
import { request } from "../api"

async function fetchReception(jobId: string): Promise<Reception | null> {
  try {
    return await request<Reception>({
      url: populateRoute(Route.RECEPTION_CONTROLLER__FIND_ONE_BY_JOB_ID, jobId),
      method: "GET",
    })
  } catch (_e) {
    return null
  }
}

async function fetchReceptionDataForBooking(
  storeId: string,
  jobId: string,
): Promise<DataRequiredForReception> {
  const url = jobId
    ? populateRoute(
        Route.RECEPTION_CONTROLLER__FIND_RECEPTION_DATA_FOR_STORE_WITH_BOOKING,
        storeId,
        jobId,
      )
    : populateRoute(
        Route.RECEPTION_CONTROLLER__FIND_RECEPTION_DATA_FOR_STORE,
        storeId,
      )
  return await request<DataRequiredForReception>({
    url,
    method: "GET",
  })
}

export const receptionDataQueryOptions = (storeId: string) =>
  queryOptions({
    queryKey: ["reception-data", storeId],
    queryFn: async () => {
      const receptionData = await fetchReceptionDataForBooking(storeId, "")

      const fees = await request<ServiceFee[]>({
        url: populateRoute(
          Route.SERVICE_JOB_FEE_CONTROLLER__FIND_MANY_BY_STORE_ID,
          storeId,
        ),
        method: "GET",
      })

      const defaultFees = fees
        .filter(
          (fee) =>
            fee.appendByDefault ||
            (fee.standardFeeType === StandardFeeType.CONSUMABLES &&
              fee.isActive) ||
            (fee.standardFeeType === StandardFeeType.ENVIRONMENTAL &&
              fee.isActive),
        )
        .map((fee) => ({
          id: fee.id,
          name: fee.name,
        }))

      return { ...receptionData, defaultFees }
    },
  })

export interface NewReceptionDefaultValues {
  firstName: string
  lastName: string
  phoneNumber: string
  email: string
  agreedToJoinStoreClub: boolean
  selectedWashId: string | null
  bikeType: ServiceBikeType
  customBikeTypeId: string | null
  bikeBrandId: string | null
  bikeModel: string
  bikeColor: { value: string; label: string } | undefined
  bikeIsElectric: boolean
  packages: string[]
  manualServiceActions: ManualServiceActionInReception[]
  bookedCustomItems: BookedCustomItem[]
  mechanicId: string
  assignedMechanicId: string | null
  discountCodeId: string | undefined
  discountCode: DiscountCodeOnJob | null
  laborDiscount: Discount | null
  productsDiscount: Discount | null
  orderDiscount: Discount | null
  packageDiscounts: Record<string, Discount | null>
  fees: { id: string; name: string }[]
  customerBudget: number | null
}

interface NewReceptionDataWithBooking {
  receptionData: DataRequiredForReception
  defaultValues: NewReceptionDefaultValues
  bookingJobId: string
}

export const newReceptionDataForBookingQueryOptions = (
  storeId: string,
  jobId: string,
) =>
  queryOptions({
    queryKey: ["new-reception-data-booking", storeId, jobId],
    queryFn: async (): Promise<NewReceptionDataWithBooking> => {
      const receptionData = await fetchReceptionDataForBooking(storeId, jobId)

      const fees = await request<ServiceFee[]>({
        url: populateRoute(
          Route.SERVICE_JOB_FEE_CONTROLLER__FIND_MANY_BY_STORE_ID,
          storeId,
        ),
        method: "GET",
      })

      const {
        bike,
        customerInfoFromBooking,
        assignedMechanicId,
        defaultReceivingMechanicId,
        discountCode,
      } = receptionData

      const isEmergency = customerInfoFromBooking?.isEmergency ?? false
      const isCargoBike = bike?.type === ServiceBikeType.CARGO
      const defaultFees = fees
        .filter(
          (fee) =>
            fee.appendByDefault ||
            (fee.standardFeeType === StandardFeeType.CONSUMABLES &&
              fee.isActive) ||
            (fee.standardFeeType === StandardFeeType.ENVIRONMENTAL &&
              fee.isActive) ||
            (fee.standardFeeType === StandardFeeType.EBIKE_ADDON &&
              fee.isActive &&
              bike?.isElectric === true &&
              !isCargoBike) ||
            (fee.standardFeeType === StandardFeeType.CARGO_BIKE_ADDON &&
              fee.isActive &&
              isCargoBike) ||
            (fee.standardFeeType === StandardFeeType.EMERGENCY_SERVICE &&
              fee.isActive &&
              isEmergency),
        )
        .map((fee) => ({
          id: fee.id,
          name: fee.name,
        }))

      const defaultValues: NewReceptionDefaultValues = {
        firstName: customerInfoFromBooking?.customer.firstName ?? "",
        lastName: customerInfoFromBooking?.customer.lastName ?? "",
        phoneNumber: customerInfoFromBooking?.customer.phoneNumber ?? "",
        email: customerInfoFromBooking?.customer.email ?? "",
        agreedToJoinStoreClub:
          customerInfoFromBooking?.customerWantsToJoinStoreClub ?? false,
        selectedWashId: customerInfoFromBooking?.selectedWashId ?? null,
        bikeType: bike?.type ?? ServiceBikeType.CITY,
        customBikeTypeId: bike?.customBikeTypeId ?? null,
        bikeBrandId: bike?.bikeBrandId ?? null,
        bikeModel: bike?.model ?? "",
        bikeColor: bike?.color
          ? { value: bike.color, label: bike.color }
          : undefined,
        bikeIsElectric: bike?.isElectric ?? false,
        packages: customerInfoFromBooking?.selectedPackageIds ?? [],
        manualServiceActions: (
          customerInfoFromBooking?.bookedCustomItems ?? []
        ).map((bookedItem) => bookedItem.manualServiceAction),
        bookedCustomItems: customerInfoFromBooking?.bookedCustomItems ?? [],
        packageDiscounts: Object.fromEntries(
          (customerInfoFromBooking?.selectedPackageIds ?? [])
            .map((packageId) => {
              const pkg = receptionData.availableServicePackages.find(
                (p) => p.id === packageId,
              )
              return pkg?.defaultDiscount != null
                ? [packageId, pkg.defaultDiscount]
                : null
            })
            .filter((entry): entry is [string, Discount] => entry !== null),
        ),
        mechanicId: defaultReceivingMechanicId ?? "",
        assignedMechanicId: assignedMechanicId ?? null,
        discountCodeId: discountCode?.id,
        discountCode: discountCode ?? null,
        laborDiscount:
          discountCode?.appliesTo === DiscountAppliesTo.LABOR
            ? discountCode.discount
            : null,
        productsDiscount:
          discountCode?.appliesTo === DiscountAppliesTo.PRODUCTS
            ? discountCode.discount
            : null,
        orderDiscount:
          discountCode?.appliesTo === DiscountAppliesTo.BOTH
            ? discountCode.discount
            : null,
        fees: defaultFees,
        customerBudget: customerInfoFromBooking?.customerBudget ?? 1,
      }

      return {
        receptionData,
        defaultValues,
        bookingJobId: jobId,
      }
    },
  })

const receptionQueries = {
  all: () => ["reception"],
  details: () => [...receptionQueries.all(), "detail"],
  detail: (id: string) =>
    queryOptions({
      queryKey: [...receptionQueries.details(), id],
      queryFn: () => fetchReception(id),
    }),
}

export default receptionQueries
